import React, { useMemo, useState } from 'react';
import { NetworkStatus } from '@apollo/client';
import { client, clientWithoutAuth } from 'apollo/clients';
import { pxToRem } from 'assets/styles/convertors';
import CardGrid from 'components/Cards/CardGrid';
import { ChallengeCard } from 'components/Cards/ChallengeCard';
import Container from 'components/Container/Container';
import PageHeader from 'components/PageHeader/PageHeader';
import TabView from 'components/TabView/TabView';
import UI from 'constants/ui';
import {
  Challenge,
  GetChallengesQuery,
  GetUserChallengesCompletedQuery,
  useGetChallengesQuery,
  useGetUserChallengesCompletedQuery,
} from 'generated/graphql';
import { useIsLoggedIn } from 'hooks/auth/useIsLoggedIn';
import useBreakpoint, { Breakpoint } from 'hooks/interface/useBreakpoint';
import { useSkeleton } from 'hooks/useSkeletons';
import { useTranslation } from 'next-i18next';

import { TabItem } from '../../components/TabView/TabView.type';

import { ChallengeEmptyState } from './ChallengeEmptyState';

import styles from './BrowsingChallenges.module.scss';

enum ChallengeTab {
  LIVE = 'LIVE',
  FINISHED = 'FINISHED',
  MY_COMPLETED = 'MY_COMPLETED',
}

const columns: Record<Breakpoint, number> = {
  [Breakpoint.XS]: 2,
  [Breakpoint.SM]: 2,
  [Breakpoint.MD]: 4,
  [Breakpoint.LG]: 5,
  [Breakpoint.XL]: 6,
};

const DEFAULT_CHALLENGES_LIMIT = 24;

const BrowseChallenges = () => {
  const { t } = useTranslation();
  const { lessThanBreakpoint, breakpoint } = useBreakpoint();
  const isLoggedIn = useIsLoggedIn();

  const TABS: TabItem[] = useMemo(
    () => [
      {
        id: ChallengeTab.LIVE,
        name: 'tabLive',
      },
      {
        id: ChallengeTab.FINISHED,
        name: 'tabFinished',
      },
      {
        id: ChallengeTab.MY_COMPLETED,
        name: 'tabMyCompleted',
        disabled: !isLoggedIn,
      },
    ],
    [isLoggedIn],
  );

  const [activeTab, setActiveTab] = useState(TABS[0].id);

  const {
    data: challengesData,
    loading: isLoadingChallenges,
    fetchMore: fetchMoreChallenges,
    networkStatus,
  } = useGetChallengesQuery({
    fetchPolicy: 'cache-and-network',
    skip: activeTab === ChallengeTab.MY_COMPLETED,
    notifyOnNetworkStatusChange: true,
    client: isLoggedIn ? client : clientWithoutAuth,
    variables: {
      completed: activeTab === ChallengeTab.FINISHED,
      first: DEFAULT_CHALLENGES_LIMIT,
    },
  });

  const {
    data: userChallenges,
    loading: isLoadingUserChallenges,
    fetchMore: fetchMoreUserChallenges,
  } = useGetUserChallengesCompletedQuery({
    fetchPolicy: 'cache-and-network',
    skip: activeTab !== ChallengeTab.MY_COMPLETED,
    notifyOnNetworkStatusChange: true,
    client,
    variables: {
      first: DEFAULT_CHALLENGES_LIMIT,
    },
  });

  const switchTab = (e: React.TouchEvent | React.MouseEvent) => {
    const tabId = e.currentTarget.id;
    const disabled = TABS.find(tab => tab.id === tabId)?.disabled;
    if (!disabled) {
      setActiveTab(tabId);
    }
  };

  const gap = lessThanBreakpoint(Breakpoint.LG)
    ? `${pxToRem(20)} ${pxToRem(19)}`
    : `${pxToRem(17)} ${pxToRem(16)}`;

  const isLoading =
    activeTab === ChallengeTab.MY_COMPLETED ? isLoadingUserChallenges : isLoadingChallenges;

  const challenges =
    challengesData?.challenges?.nodes ??
    userChallenges?.challengeCompletedByCurrentUser?.nodes ??
    [];
  const filtered = challenges.filter(item => !!item?.game?.image?.key);
  const totalChallenges =
    challengesData?.challenges.totalCount ??
    userChallenges?.challengeCompletedByCurrentUser?.totalCount ??
    0;
  const canLoadMoreChallenges = challenges.length < totalChallenges;
  const initLoading =
    isLoading && [NetworkStatus.loading, NetworkStatus.setVariables].includes(networkStatus);
  const polyfillGames = useSkeleton();
  const cards = initLoading
    ? (polyfillGames as
        | GetChallengesQuery['challenges']['nodes']
        | GetUserChallengesCompletedQuery['challengeCompletedByCurrentUser']['nodes']) || []
    : filtered;

  const loadMoreChallengesHandler = async () => {
    await fetchMoreChallenges({
      variables: { skip: challenges.length },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const challenges = previousResult?.challenges?.nodes ?? [];
        const incomingChallenges = fetchMoreResult?.challenges?.nodes ?? [];

        return {
          challenges: {
            ...previousResult.challenges,
            nodes: [...challenges, ...incomingChallenges],
          },
        };
      },
    });
  };

  const loadMoreUserChallengesHandler = async () => {
    await fetchMoreUserChallenges({
      variables: { skip: challenges.length },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const challenges = previousResult?.challengeCompletedByCurrentUser?.nodes ?? [];
        const incomingChallenges = fetchMoreResult?.challengeCompletedByCurrentUser?.nodes ?? [];

        return {
          challengeCompletedByCurrentUser: {
            ...previousResult.challengeCompletedByCurrentUser,
            nodes: [...challenges, ...incomingChallenges],
          },
        };
      },
    });
  };

  const emptyStateText = useMemo(() => {
    if (activeTab === ChallengeTab.MY_COMPLETED) {
      return t('txtUserChallengeEmpty');
    }

    if (activeTab === ChallengeTab.FINISHED) {
      return t('txtChallengeFinishedEmpty');
    }

    return t('txtUserChallengeEmpty');
  }, [activeTab, t]);

  const columnOfRow = columns[breakpoint ?? Breakpoint.MD];
  const initialRows = Math.ceil(cards.length / columnOfRow);
  const countLastRowItems = cards.length - (initialRows - 1) * columnOfRow;
  const lastRowItemsHideFooter = cards.slice(-countLastRowItems).map(item => item.id);

  return (
    <div>
      <PageHeader
        className={styles.browsingChallengeHeader}
        pageName={t('navChallenges')}
        capitalize
      />

      <Container
        padding={{
          mobile: `0 ${UI.page.padding.mobile}`,
          desktop: `0 ${UI.page.padding.desktop}`,
        }}
      >
        <TabView
          className={styles.tabList}
          tabs={TABS}
          activeTab={activeTab}
          onClick={switchTab}
          setActiveTab={setActiveTab}
        />

        <CardGrid
          columns={columns}
          loading={isLoading}
          entries={totalChallenges}
          canLoadMore={canLoadMoreChallenges}
          onClickLoadMore={
            activeTab === ChallengeTab.MY_COMPLETED
              ? loadMoreUserChallengesHandler
              : loadMoreChallengesHandler
          }
          emptyState={<ChallengeEmptyState text={emptyStateText} />}
          gap={gap}
          customDisplayingText={t('txtChallengesDisplayed', { count: cards.length })}
        >
          {cards.map(item => (
            <ChallengeCard
              key={item?.id}
              challenge={item as Challenge}
              initLoading={initLoading}
              isHideFooter={canLoadMoreChallenges && lastRowItemsHideFooter.includes(item?.id)}
            />
          ))}
        </CardGrid>
      </Container>
    </div>
  );
};

export default BrowseChallenges;
